import './wego-input'

import { Input, InputChangePayload, InputInputPayload } from '@here/hds-input'
import { HDSInputProperties } from '@here/hds-react-components/Input/HDSInput'
import React, { DetailedHTMLProps, HTMLAttributes, useEffect, useImperativeHandle } from 'react'

interface CustomInputProperties extends HDSInputProperties {
  onInput?: (e: CustomEvent<InputInputPayload>) => void
  onChange?: (e: CustomEvent<InputChangePayload>) => void
  onFocus?: (e: CustomEvent<Input>) => void
  onBlur?: (e: CustomEvent<Input>) => void
  onKeydown?: (e: KeyboardEvent) => void
  onKeyup?: (e: KeyboardEvent) => void
  selectOnFocus?: boolean
  autoFocus?: boolean
}
export const WeGoInput = React.forwardRef((props: CustomInputProperties, ref) => {
  const {
    onInput,
    onChange,
    onFocus,
    onBlur,
    onKeyDown,
    onKeyUp,
    selectOnFocus,
    autoFocus,
    ...rest
  } = props
  const innerRef = React.useRef<HTMLInputElement>(null)
  useImperativeHandle(ref, () => innerRef.current)

  useEffect(() => {
    const currentRef = innerRef.current
    let timeOut: NodeJS.Timeout

    if (autoFocus !== undefined && currentRef !== undefined) {
      // add focus or blur into event loop since the input reference from HDS is not synchronously created
      timeOut = setTimeout(() => (autoFocus ? currentRef?.focus() : currentRef?.blur()))
    }

    return () => clearTimeout(timeOut)
  }, [autoFocus])

  useEffect(() => {
    const _handleInput = (e: CustomEvent<InputInputPayload>): void => onInput && onInput(e)
    const currentRef = innerRef.current
    if (onInput && currentRef) {
      currentRef?.addEventListener('input', _handleInput as EventListener)
    }
    return () => currentRef?.removeEventListener('input', _handleInput as EventListener)
  }, [onInput, innerRef])

  useEffect(() => {
    const _handleChange = (e: CustomEvent<InputChangePayload>): void => {
      onChange && onChange(e)
    }
    const currentRef = innerRef.current
    if (onChange && currentRef) {
      currentRef?.addEventListener('change', _handleChange as EventListener)
    }
    return () => currentRef?.removeEventListener('change', _handleChange as EventListener)
  }, [onChange, innerRef])

  useEffect(() => {
    const _handleFocus = (e: CustomEvent<Input>): void => {
      if (autoFocus !== undefined && autoFocus) {
        return
      }

      const value = (currentRef as HTMLInputElement).value.trim()
      if (selectOnFocus !== undefined && selectOnFocus && (value !== '' || value !== undefined)) {
        currentRef?.setSelectionRange(0, value.length)
      }

      onFocus && onFocus(e)
    }
    const currentRef = innerRef.current

    if (onFocus && currentRef) {
      currentRef?.addEventListener('focus', _handleFocus as EventListener)
    }
    return () => currentRef?.removeEventListener('focus', _handleFocus as EventListener)
  }, [onFocus, innerRef, selectOnFocus, autoFocus])

  useEffect(() => {
    const _handleBlur = (e: CustomEvent<Input>): void => {
      if (autoFocus !== undefined && !autoFocus) {
        return
      }

      onBlur && onBlur(e)
    }
    const currentRef = innerRef.current
    if (onBlur && currentRef) {
      currentRef?.addEventListener('blur', _handleBlur as EventListener)
    }
    return () => currentRef?.removeEventListener('blur', _handleBlur as EventListener)
  }, [onBlur, innerRef, autoFocus])

  useEffect(() => {
    const _handleKeydown = (e: KeyboardEvent): void => onKeyDown && onKeyDown(e)
    const currentRef = innerRef.current
    if (onKeyDown && currentRef) {
      currentRef?.addEventListener('keypress', _handleKeydown as EventListener)
    }
    return () => currentRef?.removeEventListener('keypress', _handleKeydown as EventListener)
  }, [onKeyDown, innerRef])

  useEffect(() => {
    const _handleKeyup = (e: KeyboardEvent): void => onKeyUp && onKeyUp(e)
    const currentRef = innerRef.current
    if (onKeyUp && currentRef) {
      currentRef?.addEventListener('keyup', _handleKeyup as EventListener)
    }
    return () => currentRef?.removeEventListener('keyup', _handleKeyup as EventListener)
  }, [onKeyUp, innerRef])

  return (
    <wego-input
      ref={innerRef}
      {...(rest as DetailedHTMLProps<HTMLAttributes<HTMLElement>, HTMLElement>)}
    ></wego-input>
  )
})

WeGoInput.displayName = 'WeGoInput'
